import { zodResolver } from '@hookform/resolvers/zod';
import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useFieldError, useT } from '@kmgestao/i18n';
import { AuthShell, Button, Input, Label, toast } from '@kmgestao/ui';

import { Brand } from '../../components/Brand.js';
import { CaptchaField, type CaptchaFieldHandle } from '../../features/auth/CaptchaField.js';
import { mapAuthErrorKey } from '../../features/auth/mapAuthError.js';
import { signInSchema, type SignInInput } from '../../features/auth/schemas.js';
import { useSignIn } from '../../features/auth/useSignIn.js';
import { featureFlags } from '../../lib/featureFlags.js';
import { SupportFooter } from '../../components/SupportFooter.js';

interface LoginLocationState {
  from?: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const captchaRef = useRef<CaptchaFieldHandle>(null);
  const signIn = useSignIn();
  const { t } = useT('auth');
  const tError = useFieldError();

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignInInput>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: '', password: '', captchaToken: '' },
  });

  const emailError = tError(errors.email);
  const passwordError = tError(errors.password);
  const captchaError = tError(errors.captchaToken);
  const rootError = tError(errors.root);

  const onSubmit = handleSubmit((values) => {
    signIn.mutate(values, {
      onSuccess: () => {
        toast.success(t('login.welcome'));
        const state = location.state as LoginLocationState | null;
        navigate(state?.from ?? '/dashboard', { replace: true });
      },
      onError: (error) => {
        captchaRef.current?.reset();
        setValue('captchaToken', '');
        const description = t(mapAuthErrorKey(error));
        setError('root', { message: mapAuthErrorKey(error) });
        toast.error(t('login.errorTitle'), { description });
      },
    });
  });

  return (
    <AuthShell brand={<Brand onDark className="h-20 sm:h-24" />} footer={<SupportFooter onDark />}>
      <div className="space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">{t('login.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('login.subtitle')}</p>
        </div>

        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          <div className="space-y-2">
            <Label htmlFor="login-email">{t('login.email')}</Label>
            <Input
              id="login-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder={t('login.emailPlaceholder')}
              hasError={!!errors.email}
              {...register('email')}
            />
            {emailError ? (
              <p className="text-sm text-destructive" role="alert">
                {emailError}
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="login-password">{t('login.password')}</Label>
              <Link className="text-sm font-medium text-primary hover:underline" to="/reset">
                {t('login.forgotPassword')}
              </Link>
            </div>
            <Input
              id="login-password"
              type="password"
              autoComplete="current-password"
              placeholder={t('login.passwordPlaceholder')}
              hasError={!!errors.password}
              {...register('password')}
            />
            {passwordError ? (
              <p className="text-sm text-destructive" role="alert">
                {passwordError}
              </p>
            ) : null}
          </div>

          <CaptchaField
            ref={captchaRef}
            onVerify={(token) => setValue('captchaToken', token, { shouldValidate: true })}
            onExpire={() => setValue('captchaToken', '', { shouldValidate: true })}
            error={captchaError}
          />

          {rootError ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
            >
              {rootError}
            </div>
          ) : null}

          <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? t('login.submitting') : t('login.submit')}
          </Button>
        </form>

        {featureFlags.enableSignup ? (
          <p className="text-center text-sm text-muted-foreground">
            {t('login.firstAccess')}{' '}
            <Link to="/signup" className="font-medium text-primary hover:underline">
              {t('login.createAccount')}
            </Link>
          </p>
        ) : null}
      </div>
    </AuthShell>
  );
}
